import { Box } from "@chakra-ui/react";
import {
  differenceInCalendarWeeks,
  eachDayOfInterval,
  endOfYear,
  format,
  getDay,
  isAfter,
  startOfYear,
} from "date-fns";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface DayEntry {
  date: string;
  count: number;
  sportTypes: string[];
}

interface ActivityHeatmapProps {
  days: DayEntry[];
  year: number;
}

const COLORS = [
  "rgba(13, 13, 21, 0.6)", // 0 activities
  "rgba(0, 255, 255, 0.25)", // 1 activity
  "rgba(0, 255, 255, 0.55)", // 2 activities
  "rgba(0, 255, 255, 1.0)", // 3+ activities
];

const CELL_SIZE = 12;
const CELL_GAP = 2;
const CELL_PITCH = CELL_SIZE + CELL_GAP;
const HEADER_HEIGHT = 16;
const DAY_LABEL_WIDTH = 28;
const SVG_WIDTH = DAY_LABEL_WIDTH + 53 * CELL_PITCH;
const SVG_HEIGHT = HEADER_HEIGHT + 7 * CELL_PITCH;

const DAY_LABEL_INDICES = [0, 2, 4] as const; // Mon, Wed, Fri rows

function getColorForCount(count: number): string {
  if (count === 0) return COLORS[0];
  if (count === 1) return COLORS[1];
  if (count === 2) return COLORS[2];
  return COLORS[3];
}

/** Convert Sunday-start day index (0=Sun) to Monday-start (0=Mon). */
function toMondayStart(dayIndex: number): number {
  return (dayIndex + 6) % 7;
}

interface TooltipState {
  text: string;
  x: number;
  y: number;
}

const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({ days, year }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const monthLabels = t("DashboardMonths").split(",");
  const dayLabels = t("DashboardDays").split(",");

  const { cells, monthPositions } = useMemo(() => {
    const yearStart = startOfYear(new Date(year, 0, 1));
    const yearEnd = endOfYear(new Date(year, 0, 1));
    const allDays = eachDayOfInterval({ start: yearStart, end: yearEnd });
    const today = new Date();

    // Build lookup from days prop
    const lookup = new Map<string, { count: number; sportTypes: string[] }>();
    for (const day of days) {
      lookup.set(day.date, { count: day.count, sportTypes: day.sportTypes });
    }

    // Track first Monday position for each month
    const monthFirstWeek = new Map<number, number>();

    const cellData = allDays.map((day) => {
      const dateStr = format(day, "yyyy-MM-dd");
      const row = toMondayStart(getDay(day));
      const col = differenceInCalendarWeeks(day, yearStart, { weekStartsOn: 1 });
      const entry = lookup.get(dateStr);
      const count = entry?.count ?? 0;
      const sportTypes = entry?.sportTypes ?? [];
      const isFuture = isAfter(day, today);

      // Track first Monday of each month for labels
      const month = day.getMonth();
      if (row === 0 && !monthFirstWeek.has(month)) {
        monthFirstWeek.set(month, col);
      }

      const displayDate = format(day, "MMM d");
      const tooltipText = isFuture
        ? displayDate
        : count > 0
          ? `${displayDate} \u2014 ${count} activit${count === 1 ? "y" : "ies"}: ${sportTypes.join(", ")}`
          : `${displayDate} \u2014 No activities`;

      return {
        key: dateStr,
        x: DAY_LABEL_WIDTH + col * CELL_PITCH,
        y: HEADER_HEIGHT + row * CELL_PITCH,
        fill: isFuture ? "transparent" : getColorForCount(count),
        stroke: isFuture ? "rgba(255, 255, 255, 0.05)" : "none",
        tooltip: tooltipText,
      };
    });

    // Build month label positions
    const positions = monthLabels.map((label, idx) => {
      const weekCol = monthFirstWeek.get(idx);
      if (weekCol === undefined) return null;
      return {
        label,
        x: DAY_LABEL_WIDTH + weekCol * CELL_PITCH,
      };
    }).filter((p): p is { label: string; x: number } => p !== null);

    return { cells: cellData, monthPositions: positions };
  }, [days, year, monthLabels]);

  const handleMouseEnter = useCallback(
    (text: string, e: React.MouseEvent<SVGRectElement>) => {
      const container = containerRef.current;
      if (!container) return;
      const containerRect = container.getBoundingClientRect();
      const cellRect = e.currentTarget.getBoundingClientRect();
      setTooltip({
        text,
        x: cellRect.left - containerRect.left + cellRect.width / 2,
        y: cellRect.top - containerRect.top,
      });
    },
    [],
  );

  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  return (
    <Box ref={containerRef} overflowX="auto" position="relative">
      <svg
        viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
        width="100%"
        role="img"
        aria-label={t("DashboardHeatmapLabel", { year })}
      >
        {/* Month labels */}
        {monthPositions.map((mp) => (
          <text
            key={mp.label}
            x={mp.x}
            y={12}
            fill="rgba(255, 255, 255, 0.5)"
            fontSize="10"
            fontFamily="sans-serif"
          >
            {mp.label}
          </text>
        ))}

        {/* Day labels (Mon/Wed/Fri rows) */}
        {DAY_LABEL_INDICES.map((rowIdx, i) => (
          <text
            key={rowIdx}
            x={0}
            y={HEADER_HEIGHT + rowIdx * CELL_PITCH + CELL_SIZE - 2}
            fill="rgba(255, 255, 255, 0.5)"
            fontSize="9"
            fontFamily="sans-serif"
          >
            {dayLabels[i]}
          </text>
        ))}

        {/* Day cells */}
        {cells.map((cell) => (
          <rect
            key={cell.key}
            x={cell.x}
            y={cell.y}
            width={CELL_SIZE}
            height={CELL_SIZE}
            rx={2}
            ry={2}
            fill={cell.fill}
            stroke={cell.stroke}
            strokeWidth={cell.stroke !== "none" ? 1 : 0}
            onMouseEnter={(e) => handleMouseEnter(cell.tooltip, e)}
            onMouseLeave={handleMouseLeave}
          />
        ))}
      </svg>

      {/* Single shared tooltip — O(1) instead of 365 Popper instances */}
      {tooltip && (
        <Box
          position="absolute"
          left={`${tooltip.x}px`}
          top={`${tooltip.y}px`}
          transform="translate(-50%, -100%) translateY(-6px)"
          bg="gray.800"
          color="white"
          px={2}
          py={1}
          borderRadius="md"
          fontSize="xs"
          whiteSpace="nowrap"
          pointerEvents="none"
          zIndex={10}
          boxShadow="0 2px 8px rgba(0, 0, 0, 0.4)"
        >
          {tooltip.text}
        </Box>
      )}
    </Box>
  );
};

export default ActivityHeatmap;
